import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Video, Square, Loader2, CheckCircle2, RefreshCcw, SwitchCamera } from "lucide-react";
import { toast } from "sonner";

/**
 * Live video capture (device camera + mic) with 2 MB size cap.
 * Auto-stops when the recorded blob is approaching 2 MB. Uploads via /api/uploads.
 * On mobile, prefers the rear (environment) camera; toggle available to flip.
 */
const MAX_BYTES = 2 * 1024 * 1024; // 2 MB
const HARD_STOP_SECONDS = 30;      // upper bound

export default function VideoRecorder({ onComplete }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading | done | error
  const [elapsed, setElapsed] = useState(0);
  const [sizeKb, setSizeKb] = useState(0);
  const [uploaded, setUploaded] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [facing, setFacing] = useState("environment"); // "environment" (rear) | "user" (front)

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const totalBytesRef = useRef(0);
  const startedAtRef = useRef(0);
  const timerRef = useRef(null);
  const videoRef = useRef(null);

  const cleanupStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => () => {
    cleanupStream();
    if (timerRef.current) clearInterval(timerRef.current);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pickMime = () => {
    const options = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
      "video/mp4",
    ];
    for (const m of options) {
      if (MediaRecorder.isTypeSupported(m)) return m;
    }
    return "";
  };

  const start = async () => {
    try {
      // Prefer rear camera on mobile; fall back to any camera if unavailable.
      const constraints = {
        video: {
          facingMode: { ideal: facing },
          width: { ideal: 640 },
          height: { ideal: 480 },
          frameRate: { ideal: 24 },
        },
        audio: true,
      };
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(constraints);
      } catch (e) {
        // Retry without facingMode (some desktops/laptops don't support it)
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 24 } },
          audio: true,
        });
      }
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        await videoRef.current.play().catch(() => {});
      }
      const mime = pickMime();
      const rec = new MediaRecorder(stream, {
        mimeType: mime || undefined,
        videoBitsPerSecond: 500_000,   // 500 kbps
        audioBitsPerSecond: 64_000,    // 64 kbps
      });
      recorderRef.current = rec;
      chunksRef.current = [];
      totalBytesRef.current = 0;
      setSizeKb(0);
      setElapsed(0);
      rec.ondataavailable = (e) => {
        if (!e.data || !e.data.size) return;
        totalBytesRef.current += e.data.size;
        chunksRef.current.push(e.data);
        setSizeKb(Math.round(totalBytesRef.current / 1024));
        if (totalBytesRef.current >= MAX_BYTES - 50_000) {
          // Auto-stop close to limit
          try { rec.stop(); } catch (_) { /* ignore */ }
        }
      };
      rec.onstop = handleStop;
      rec.start(250); // chunk every 250ms to check size fast

      startedAtRef.current = performance.now();
      timerRef.current = setInterval(() => {
        const e = Math.floor((performance.now() - startedAtRef.current) / 1000);
        setElapsed(e);
        if (e >= HARD_STOP_SECONDS && rec.state !== "inactive") {
          try { rec.stop(); } catch (_) { /* ignore */ }
        }
      }, 250);
      setState("recording");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Camera / microphone access denied");
      setState("error");
    }
  };

  const stop = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  };

  const handleStop = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = null;
    cleanupStream();
    if (videoRef.current) videoRef.current.srcObject = null;

    setState("uploading");
    const blob = new Blob(chunksRef.current, { type: recorderRef.current?.mimeType || "video/webm" });
    if (blob.size === 0) {
      setState("idle");
      return;
    }
    if (blob.size > MAX_BYTES) {
      toast.error(`Recording is ${Math.round(blob.size / 1024)} KB — exceeds 2 MB limit. Try a shorter clip.`);
      setState("idle");
      return;
    }
    try {
      const rawType = blob.type || "video/webm";
      // Strip codec params — server matches on primary MIME only
      const cleanType = rawType.split(";", 1)[0].trim() || "video/webm";
      const ext = cleanType.includes("mp4") ? "mp4" : "webm";
      const file = new File([blob], `noise-video-${Date.now()}.${ext}`, { type: cleanType });
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const localUrl = URL.createObjectURL(blob);
      setPreviewUrl(localUrl);
      setUploaded(data);
      setState("done");
      onComplete?.(data);
      toast.success("Video captured & attached");
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(typeof detail === "string" ? detail : "Video upload failed");
      setState("error");
    }
  };

  const clear = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setUploaded(null);
    setState("idle");
    setSizeKb(0);
    setElapsed(0);
  };

  return (
    <div className="rounded-lg border border-border bg-slate-50 p-4 space-y-3" data-testid="video-recorder">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="overline text-slate-500">Live video capture</div>
          <div className="text-xs text-muted-foreground mt-1">Max 2 MB (auto-stops near limit). Best for short evidence clips.</div>
        </div>
        <div className="flex items-center gap-2">
          {state !== "recording" && state !== "uploading" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setFacing((f) => (f === "environment" ? "user" : "environment"))}
              className="gap-1.5 h-8 px-2 text-xs"
              data-testid="vr-camera-toggle"
              title="Switch between rear and front camera"
            >
              <SwitchCamera className="w-3.5 h-3.5" />
              {facing === "environment" ? "Rear" : "Front"}
            </Button>
          )}
          {state === "recording" && (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-100 text-red-800 text-xs font-medium" data-testid="vr-rec-indicator">
              <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" /> REC · {elapsed}s · {sizeKb} KB
            </span>
          )}
        </div>
      </div>

      {(state === "recording" || state === "idle" || state === "error") && (
        <div className="aspect-video w-full bg-black/90 rounded-md overflow-hidden">
          <video ref={videoRef} className="w-full h-full object-cover" playsInline />
        </div>
      )}

      {state === "done" && previewUrl && (
        <div className="space-y-2">
          <video src={previewUrl} controls className="w-full rounded-md" data-testid="vr-preview" />
          <div className="flex items-center gap-2 text-sm text-emerald-800">
            <CheckCircle2 className="w-4 h-4" />
            <span>Attached — {uploaded?.filename} ({Math.round((uploaded?.size || 0) / 1024)} KB)</span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {state === "idle" && (
          <Button type="button" onClick={start} className="gap-2" data-testid="vr-start-btn">
            <Video className="w-4 h-4" /> Start recording
          </Button>
        )}
        {state === "recording" && (
          <Button type="button" onClick={stop} variant="destructive" className="gap-2" data-testid="vr-stop-btn">
            <Square className="w-4 h-4" /> Stop &amp; upload
          </Button>
        )}
        {state === "uploading" && (
          <Button type="button" disabled className="gap-2" data-testid="vr-uploading-btn">
            <Loader2 className="w-4 h-4 animate-spin" /> Uploading...
          </Button>
        )}
        {state === "done" && (
          <Button type="button" onClick={clear} variant="outline" className="gap-2" data-testid="vr-record-again-btn">
            <RefreshCcw className="w-4 h-4" /> Record another
          </Button>
        )}
        {state === "error" && (
          <Button type="button" onClick={start} variant="outline" className="gap-2" data-testid="vr-retry-btn">
            <Video className="w-4 h-4" /> Retry
          </Button>
        )}
      </div>
    </div>
  );
}
